import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLocale, formatLocaleCurrency, parseLocaleNumber } from "@/hooks/useLocale";

export interface UserPreferences {
  id: string;
  user_id: string;
  currency: string;
  locale: string;
  default_account: string | null;
  created_at: string;
  updated_at: string;
}

const CURRENCIES = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
];

export const useCurrencies = () => CURRENCIES;

export const useUserPreferences = () => {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  
  // Get locale from preferences or browser default
  const currentLocale = preferences?.locale || navigator.language || "en-US";
  const localeUtils = useLocale(currentLocale);

  const fetchPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from("user_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      
      if (!data) {
        // Create default preferences with detected locale
        const detectedLocale = navigator.language || "en-US";
        const { data: newPrefs, error: insertError } = await supabase
          .from("user_preferences")
          .insert({ user_id: user.id, currency: "USD", locale: detectedLocale })
          .select()
          .single();
        
        if (insertError) throw insertError;
        setPreferences(newPrefs);
      } else {
        setPreferences(data);
      }
    } catch (error) {
      console.error("Error fetching preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateCurrency = async (currency: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !preferences) return;

      const { error } = await supabase
        .from("user_preferences")
        .update({ currency })
        .eq("user_id", user.id);

      if (error) throw error;
      
      setPreferences({ ...preferences, currency });
      toast({ title: "Currency updated", description: `Your currency is now ${currency}` });
    } catch (error) {
      console.error("Error updating currency:", error);
      toast({ title: "Error", description: "Failed to update currency", variant: "destructive" });
    }
  };

  const updateLocale = async (locale: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !preferences) return;

      const { error } = await supabase
        .from("user_preferences")
        .update({ locale })
        .eq("user_id", user.id);

      if (error) throw error;
      
      setPreferences({ ...preferences, locale });
      toast({ title: "Locale updated", description: `Your locale is now ${locale}` });
    } catch (error) {
      console.error("Error updating locale:", error);
      toast({ title: "Error", description: "Failed to update locale", variant: "destructive" });
    }
  };

  const updateDefaultAccount = async (defaultAccount: string | null) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !preferences) return;

      const { error } = await supabase
        .from("user_preferences")
        .update({ default_account: defaultAccount })
        .eq("user_id", user.id);

      if (error) throw error;
      
      setPreferences({ ...preferences, default_account: defaultAccount });
      toast({ 
        title: "Default account updated", 
        description: defaultAccount ? `Default account set to ${defaultAccount}` : "Default account cleared"
      });
    } catch (error) {
      console.error("Error updating default account:", error);
      toast({ title: "Error", description: "Failed to update default account", variant: "destructive" });
    }
  };

  useEffect(() => {
    fetchPreferences();
  }, []);

  const getCurrencySymbol = (code?: string) => {
    const currency = CURRENCIES.find(c => c.code === (code || preferences?.currency || "USD"));
    return currency?.symbol || "$";
  };

  const formatCurrency = (amount: number, currencyCode?: string) => {
    const code = currencyCode || preferences?.currency || "USD";
    // formatLocaleCurrency never throws and always keeps the currency (D-062).
    return formatLocaleCurrency(amount, currentLocale, code);
  };

  const parseNumber = (value: string) => {
    return parseLocaleNumber(value, currentLocale);
  };

  return {
    preferences,
    loading,
    updateCurrency,
    updateLocale,
    updateDefaultAccount,
    getCurrencySymbol,
    formatCurrency,
    parseNumber,
    locale: currentLocale,
    decimalSeparator: localeUtils.decimalSeparator,
    thousandsSeparator: localeUtils.thousandsSeparator,
    formatNumber: localeUtils.formatNumber,
    isValidNumber: localeUtils.isValidNumber,
    getPlaceholder: localeUtils.getPlaceholder,
  };
};
